import type {
  Account,
  AiTool,
  CheckoutChoice,
  CustomTone,
  Menu,
  MenuSetting,
  Plan,
  PlansOpener,
  SavedInstruction,
  Usage,
} from '@lyrmo/shared';
import { createContext, useContext } from 'react';
import type { ApiClient } from '../../api/client';

export type AccountDialog =
  'plans' | 'sign-in' | 'billing' | 'tones' | 'instructions' | 'ai-tools' | 'api-keys';

/** An instruction typed for one rewrite, being saved for later in the instructions dialog. */
export interface InstructionDraft {
  readonly text: string;
  /** Hears about the instruction once it's saved. */
  readonly onSaved?: (instruction: SavedInstruction) => void;
}

/** A short message about the account, such as a finished checkout, shown above the editor. */
export interface Notice {
  readonly tone: 'success' | 'warning';
  readonly text: string;
  /** Offers a next step, such as requesting a new sign-in link. */
  readonly action?: { readonly label: string; readonly dialog: AccountDialog };
}

export interface AccountContextValue {
  readonly client: ApiClient;
  /** Null until the first load finishes, or if it failed. */
  readonly account: Account | null;
  /** Loaded when the plans dialog first opens, or the pricing page asks for them. */
  readonly plans: Plan[] | null;
  readonly planChangesEnabled: boolean;
  readonly plansError: string | null;
  /** The writer's own tones: none when signed out, and null until known. */
  readonly customTones: readonly CustomTone[] | null;
  /** Loads the tones again, after one is made, changed, or deleted. */
  readonly reloadCustomTones: () => Promise<void>;
  /** The writer's saved instructions: none when signed out, and null until known. */
  readonly savedInstructions: readonly SavedInstruction[] | null;
  /** Loads the saved instructions again, after one is made, changed, or deleted. */
  readonly reloadSavedInstructions: () => Promise<void>;
  /** An instruction typed for one rewrite, being saved for later in the instructions dialog. */
  readonly instructionDraft: InstructionDraft | null;
  /** The writer's own AI tools: none when signed out, and null until known. */
  readonly aiTools: readonly AiTool[] | null;
  /** Loads the AI tools again, after one is made, changed, or deleted. */
  readonly reloadAiTools: () => Promise<void>;
  /**
   * Which choices in each menu, AI tools, tones, and saved instructions, built-in or their own,
   * are hidden or pinned: as everyone starts when signed out, and null until known.
   */
  readonly menuSettings: Readonly<Record<Menu, readonly MenuSetting[] | null>>;
  /** Loads one menu's settings again, such as when the dialog managing it opens. */
  readonly reloadMenuSettings: (menu: Menu) => Promise<void>;
  /** Hides or pins a choice, at once here, then on the server; rejects if the server doesn't. */
  readonly keepMenuItem: (menu: Menu, setting: MenuSetting) => Promise<void>;
  /** Whether the AI tools dialog opened straight to making a new tool. */
  readonly newAiTool: boolean;
  /** Whether the tones dialog opened straight to making a new tone. */
  readonly newTone: boolean;
  readonly dialog: AccountDialog | null;
  readonly notice: Notice | null;
  // Function properties rather than methods: components destructure them.
  /** Opens a dialog; for the plans, `from` says what opened them, to count it. */
  readonly openDialog: (dialog: AccountDialog, from?: PlansOpener) => void;
  /** Loads the plans without opening their dialog, for the pricing page. */
  readonly loadPlans: () => void;
  /** Opens the instructions dialog to save an instruction typed for one rewrite, for later. */
  readonly saveInstruction: (draft: InstructionDraft) => void;
  /** Opens the AI tools dialog to make a new tool. */
  readonly makeAiTool: () => void;
  /** Opens the tones dialog to make a new tone. */
  readonly makeTone: () => void;
  readonly closeDialog: () => void;
  readonly notify: (notice: Notice | null) => void;
  readonly refresh: () => Promise<void>;
  /** Updates usage from a rewrite response, without another request. */
  readonly applyUsage: (usage: Usage) => void;
  readonly signOut: () => Promise<void>;
  /** Sends the browser to checkout for a plan, billed monthly or yearly. */
  readonly checkout: (choice: CheckoutChoice) => Promise<void>;
  /** Switches a subscriber to another plan or billing interval, then says so. */
  readonly changePlan: (choice: CheckoutChoice) => Promise<void>;
  /** Stops a subscriber's subscription renewing, then says so. */
  readonly cancelSubscription: () => Promise<void>;
  /** Takes back a cancellation, so the subscription renews after all, then says so. */
  readonly resumeSubscription: () => Promise<void>;
}

export const AccountContext = createContext<AccountContextValue | null>(null);

export function useAccount(): AccountContextValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccount must be used inside an AccountProvider');
  return value;
}

/** The account context, or null outside an AccountProvider (the editor works without one). */
export const useOptionalAccount = (): AccountContextValue | null => useContext(AccountContext);
